import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';

// Normalize with exactly one trailing slash
const API_BASE = ((process.env.REACT_APP_BASE_URL || "http://localhost:5000/")
  .replace(/\/$/, '')) + '/';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    password: '',
    status: 'active',
    profilePhoto: null,
    profilePreview: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch {
      Swal.fire('Error', 'Failed to load users', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      email: '',
      contact: '',
      password: '',
      status: 'active',
      profilePhoto: null,
      profilePreview: ''
    });
    setEditMode(false);
    setCurrentUser(null);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      contact: user.contact_no || '',         // NOTE: contact_no not contact
      password: '',
      status: user.status,
      profilePhoto: null,
      profilePreview: user.profile_photo || ''
    });
    setEditMode(true);
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    setFormData(fd => ({
      ...fd,
      profilePhoto: file,
      profilePreview: URL.createObjectURL(file)
    }));
  };

  // Handle Add/Edit User (multipart for photo)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || (!editMode && !formData.password)) {
      return Swal.fire('Validation Error', 'Name, Email, and Password are required.', 'error');
    }

    try {
      const submitForm = new FormData();
      submitForm.append('name', formData.name.trim());
      submitForm.append('email', formData.email.trim());
      submitForm.append('contact_no', formData.contact.trim());  // <-- match DB
      submitForm.append('status', formData.status);
      if (formData.profilePhoto) {
        submitForm.append('profile_photo', formData.profilePhoto);
      }
      // Password: only send if new or updating password
      if (formData.password || !editMode) {
        submitForm.append('password', formData.password);
      }

      if (editMode && currentUser) {
        await api.put(`/users/${currentUser.id}`, submitForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire({ icon: 'success', title: 'User updated', timer: 1200, showConfirmButton: false });
      } else {
        await api.post('/users', submitForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire({ icon: 'success', title: 'User added', timer: 1200, showConfirmButton: false });
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save user', 'error');
    }
  };

  // Delete User
  const handleDelete = (user) => {
    Swal.fire({
      title: `Delete "${user.name}"?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/users/${user.id}`);
          Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
          fetchUsers();
        } catch {
          Swal.fire('Error', 'Failed to delete user', 'error');
        }
      }
    });
  };

  return (
    <div className="p-6">
      {/* Header Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Users</h1>
        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition"
        >
          Add New User
        </button>
      </div>

      {/* Users Table */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef'
              }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Photo</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Contact</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #333',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Loading users...</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const raw = user.profile_photo || '';
                const photoUrl =
                  raw.startsWith('http')
                    ? raw
                    : raw.length > 0
                    ? `${API_BASE}${raw.replace(/^\/+/, '')}`
                    : '/placeholder.png';
                return (
                  <tr key={user.id} style={{
                    borderBottom: '1px solid #f1f3f4'
                  }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6' }}>
                        <img
                          src={photoUrl}
                          alt={user.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{user.name}</div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>{user.contact_no}</div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        borderRadius: '12px',
                        background: user.status === 'active' ? '#dcfce7' : '#fecaca',
                        color: user.status === 'active' ? '#166534' : '#dc2626'
                      }}>
                        {user.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => openEditModal(user)}
                          style={{
                            background: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >Edit</button>
                        <button
                          onClick={() => handleDelete(user)}
                          style={{
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">{editMode ? 'Edit User' : 'Add New User'}</h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">×</button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  <input
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                  />
                </div>
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {editMode ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    required={!editMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Photo {editMode ? '(leave blank to keep current)' : ''}
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.profilePreview ? (
                      <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100">
                        <img
                          src={
                            formData.profilePreview.startsWith('blob:')
                              ? formData.profilePreview
                              : formData.profilePreview.startsWith('http')
                              ? formData.profilePreview
                              : `${API_BASE}${formData.profilePreview.replace(/^\/+/, '')}`
                          }
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No image</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="block text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4 file:rounded
                        file:border-0 file:text-sm file:font-semibold
                        file:bg-black file:text-white hover:file:bg-gray-900"
                      required={!editMode}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                    disabled={isLoading}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >{editMode ? 'Update User' : 'Add User'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
